import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getPackingSlips,
  getPackingSlip,
  getShippingSummary,
  createPackingSlip,
  createPackingSlipFromWorkOrder,
  updatePackingSlip,
  updatePackingSlipItem,
  deletePackingSlip,
  preparePackingSlip,
  verifyPackingSlip,
  shipPackingSlip,
  confirmDelivery,
  cancelPackingSlip,
} from '../api';
import type {
  PackingSlipFilters,
  CreatePackingSlipInput,
  UpdatePackingSlipInput,
  UpdatePackingSlipItemInput,
  ShipPackingSlipInput,
} from '../types';
import { wmsWorkOrderKeys } from './useWmsWorkOrders';
import { wmsInventoryKeys } from './useWmsInventory';

// Helper to extract error message
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unexpected error occurred';
}

// Query keys
export const wmsShippingKeys = {
  all: ['wms-shipping'] as const,
  // No-arg call returns a prefix so invalidateQueries matches filtered lists.
  list: (filters?: PackingSlipFilters) => filters !== undefined
    ? [...wmsShippingKeys.all, 'list', filters] as const
    : [...wmsShippingKeys.all, 'list'] as const,
  detail: (id: string) => [...wmsShippingKeys.all, 'detail', id] as const,
  summary: () => [...wmsShippingKeys.all, 'summary'] as const,
};

// ============================================================================
// Packing Slip Query Hooks
// ============================================================================

export function usePackingSlips(filters: PackingSlipFilters = {}) {
  return useQuery({
    queryKey: wmsShippingKeys.list(filters),
    queryFn: () => getPackingSlips(filters),
  });
}

export function usePackingSlip(id: string) {
  return useQuery({
    queryKey: wmsShippingKeys.detail(id),
    queryFn: () => getPackingSlip(id),
    enabled: !!id,
  });
}

export function useShippingSummary() {
  return useQuery({
    queryKey: wmsShippingKeys.summary(),
    queryFn: getShippingSummary,
  });
}

// ============================================================================
// Create Packing Slip Hooks
// ============================================================================

export function useCreatePackingSlip() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePackingSlipInput) => createPackingSlip(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: wmsShippingKeys.list() });
      queryClient.invalidateQueries({ queryKey: wmsShippingKeys.summary() });
      // Invalidate work order as its status may change to 'packing'
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.detail(result.work_order_id) });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.list() });
      toast.success(`Packing slip "${result.slip_number}" created`);
    },
    onError: (error) => {
      toast.error('Failed to create packing slip', { description: getErrorMessage(error) });
    },
  });
}

export function useCreatePackingSlipFromWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (workOrderId: string) => createPackingSlipFromWorkOrder(workOrderId),
    onSuccess: (result, workOrderId) => {
      queryClient.invalidateQueries({ queryKey: wmsShippingKeys.list() });
      queryClient.invalidateQueries({ queryKey: wmsShippingKeys.summary() });
      // Invalidate work order as its status may change to 'packing'
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.detail(workOrderId) });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.list() });
      toast.success(`Packing slip "${result.slip_number}" created from work order`);
    },
    onError: (error) => {
      toast.error('Failed to create packing slip', { description: getErrorMessage(error) });
    },
  });
}

// ============================================================================
// Update Packing Slip Hooks
// ============================================================================

export function useUpdatePackingSlip() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePackingSlipInput }) =>
      updatePackingSlip(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: wmsShippingKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: wmsShippingKeys.list() });
      toast.success('Packing slip updated');
    },
    onError: (error) => {
      toast.error('Failed to update packing slip', { description: getErrorMessage(error) });
    },
  });
}

export function useUpdatePackingSlipItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ packingSlipId, itemId, data }: { packingSlipId: string; itemId: string; data: UpdatePackingSlipItemInput }) =>
      updatePackingSlipItem(packingSlipId, itemId, data),
    onSuccess: (_, { packingSlipId }) => {
      queryClient.invalidateQueries({ queryKey: wmsShippingKeys.detail(packingSlipId) });
      toast.success('Item updated');
    },
    onError: (error) => {
      toast.error('Failed to update item', { description: getErrorMessage(error) });
    },
  });
}

export function useDeletePackingSlip() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deletePackingSlip(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wmsShippingKeys.list() });
      queryClient.invalidateQueries({ queryKey: wmsShippingKeys.summary() });
      toast.success('Packing slip deleted');
    },
    onError: (error) => {
      toast.error('Failed to delete packing slip', { description: getErrorMessage(error) });
    },
  });
}

// ============================================================================
// Packing Slip Workflow Hooks
// ============================================================================

export function usePreparePackingSlip() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => preparePackingSlip(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: wmsShippingKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: wmsShippingKeys.list() });
      queryClient.invalidateQueries({ queryKey: wmsShippingKeys.summary() });
      toast.success('Packing slip ready for packing');
    },
    onError: (error) => {
      toast.error('Failed to prepare packing slip', { description: getErrorMessage(error) });
    },
  });
}

export function useVerifyPackingSlip() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => verifyPackingSlip(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: wmsShippingKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: wmsShippingKeys.list() });
      queryClient.invalidateQueries({ queryKey: wmsShippingKeys.summary() });
      toast.success('Packing slip verified');
    },
    onError: (error) => {
      toast.error('Failed to verify packing slip', { description: getErrorMessage(error) });
    },
  });
}

export function useShipPackingSlip() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ShipPackingSlipInput }) =>
      shipPackingSlip(id, data),
    onSuccess: (result, { id }) => {
      queryClient.invalidateQueries({ queryKey: wmsShippingKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: wmsShippingKeys.list() });
      queryClient.invalidateQueries({ queryKey: wmsShippingKeys.summary() });
      // Invalidate work order as item quantities shipped are updated
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.detail(result.work_order_id) });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.items(result.work_order_id) });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.serials(result.work_order_id) });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.summary() });
      // Invalidate serialized items and movements
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.serialized() });
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.movements() });
      toast.success('Shipment recorded successfully');
    },
    onError: (error) => {
      toast.error('Failed to record shipment', { description: getErrorMessage(error) });
    },
  });
}

export function useConfirmDelivery() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => confirmDelivery(id),
    onSuccess: (result, id) => {
      queryClient.invalidateQueries({ queryKey: wmsShippingKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: wmsShippingKeys.list() });
      queryClient.invalidateQueries({ queryKey: wmsShippingKeys.summary() });
      // Invalidate work order as it may be marked complete
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.detail(result.work_order_id) });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.summary() });
      toast.success('Delivery confirmed');
    },
    onError: (error) => {
      toast.error('Failed to confirm delivery', { description: getErrorMessage(error) });
    },
  });
}

export function useCancelPackingSlip() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      cancelPackingSlip(id, reason),
    onSuccess: (result, { id }) => {
      queryClient.invalidateQueries({ queryKey: wmsShippingKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: wmsShippingKeys.list() });
      queryClient.invalidateQueries({ queryKey: wmsShippingKeys.summary() });
      // Invalidate work order as status may revert
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.detail(result.work_order_id) });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.items(result.work_order_id) });
      toast.success('Packing slip cancelled');
    },
    onError: (error) => {
      toast.error('Failed to cancel packing slip', { description: getErrorMessage(error) });
    },
  });
}
